import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AcreageReportOrgDetail,
  AcreageReportSummary,
  AcreageReportFilterOptions,
  AcreagePdfExportRequest,
  AcreagePdfExportResponse
} from '../models/acreage-report.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Acreage Report Service (Admin)
 *
 * Service for managing acreage reports in the admin portal.
 * Communicates with /admin/acreage-reports endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class AcreageReportService {
  private readonly apiUrl = `${environment.apiUrl}/admin/acreage-reports`;

  constructor(private http: HttpClient) {}

  /**
   * Get organization detail acreage report
   *
   * @param organizationId - Organization ID to get report for
   * @param startDate - Report start date (YYYY-MM-DD format)
   * @param endDate - Report end date (YYYY-MM-DD format)
   * @param droneId - Optional drone ID to scope report to specific drone
   * @returns Organization detail report with drill-downs
   */
  async getOrganizationDetail(
    organizationId: string,
    startDate: string,
    endDate: string,
    droneId?: string
  ): Promise<AcreageReportOrgDetail | null> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    if (droneId) {
      params.append('droneId', droneId);
    }

    const url = `${this.apiUrl}/organization/${organizationId}?${params.toString()}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<AcreageReportOrgDetail>>(url)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  }

  /**
   * Get cross-organization acreage summary
   *
   * @param startDate - Report start date (YYYY-MM-DD format)
   * @param endDate - Report end date (YYYY-MM-DD format)
   * @returns Cross-organization summary with all organizations
   */
  async getSummary(
    startDate: string,
    endDate: string
  ): Promise<AcreageReportSummary | null> {
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);

    const url = `${this.apiUrl}/summary?${params.toString()}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<AcreageReportSummary>>(url)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return null;
  }

  /**
   * Get filter options for acreage reports
   *
   * @returns Available organizations and date range hints
   */
  async getFilters(): Promise<AcreageReportFilterOptions> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<AcreageReportFilterOptions>>(`${this.apiUrl}/filters`)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return {
      organizations: [],
      dateRangeHints: {
        minDate: null,
        maxDate: null
      }
    };
  }

  /**
   * Export acreage report to PDF
   *
   * @param reportType - Type of report (organization-detail or cross-organization-summary)
   * @param filters - Filters applied to the report
   * @param data - Report data to export
   * @returns PDF download URL
   */
  async exportPdf(
    reportType: 'organization-detail' | 'cross-organization-summary',
    filters: AcreagePdfExportRequest['filters'],
    data: AcreagePdfExportRequest['data']
  ): Promise<AcreagePdfExportResponse | null> {
    const requestBody: AcreagePdfExportRequest = {
      reportType,
      filters,
      data
    };

    const response = await firstValueFrom(
      this.http.post<ApiResponse<AcreagePdfExportResponse>>(
        `${this.apiUrl}/export-pdf`,
        requestBody
      )
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to export PDF');
  }
}
